import {
  ToolsRoles,
  isAbleToSeeUIElement,
} from '../../utils/roles/client-roles';
import { useQueryClient } from '@tanstack/react-query';
import { Dispatch, SetStateAction } from 'react';
import { Policy } from '../../../types';
import SharedGridStateIconButton from '../../shared/shared-grid-state-icon-button-latest';
import SharedToolbar from '../../shared/shared-toolbar-latest';
import { useNavigate } from 'react-router-dom';
import { pagesConfig } from '../../shared/pages-config';
import { ModuleList } from '../../shared/analytics/module-list';
import { Messages } from '../../utils/message-constants';
import CreateButton from '../../shared/buttons/create-button';
import SharedIconButton from '../../shared/buttons/shared-icon-button';

interface PoliciesGridToolbarProps {
  toolsRoles: string[];
  setOpenDeletePolicies: Dispatch<SetStateAction<boolean>>;
  selectedPolicies: number[];
  policyList: Policy[];
  setOpenRunDialog: Dispatch<SetStateAction<boolean>>;
}

export default function PoliciesGridToolbar({
  toolsRoles,
  setOpenDeletePolicies,
  selectedPolicies,
  policyList,
  setOpenRunDialog,
}: PoliciesGridToolbarProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const selectedPolicyObjects = policyList.filter((p) =>
    selectedPolicies.includes(p.id)
  );
  const includesNewUserPolicies = selectedPolicyObjects.some(
    (p) => p.policyType === 'newUser'
  );
  const hasRunningPolicies = selectedPolicyObjects.some(
    (p) => p.status === 'RUNNING'
  );
  const nonNewUserPolicies = selectedPolicyObjects.filter(
    (p) => p.policyType !== 'newUser'
  );
  const allRunnableAreRunning =
    nonNewUserPolicies.length > 0 &&
    nonNewUserPolicies.every((p) => p.status === 'RUNNING');

  const canRun = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.MaunalRunPolicy.value
  );

  function getRunTooltip() {
    if (!canRun)
      return Messages.Errors.Permission.Acting(ToolsRoles.MaunalRunPolicy.name);
    if (includesNewUserPolicies)
      return `You can't manually run New User Creation policies. Please unselect any New User Creation policies.`;
    if (allRunnableAreRunning)
      return 'All selected policies are already running.';
    if (hasRunningPolicies)
      return 'Some selected policies are already running and will be skipped.';
    return 'Run Policies';
  }

  return (
    <SharedToolbar
      primaryButtons={
        <CreateButton
          variant="contained"
          disabled={
            !isAbleToSeeUIElement(toolsRoles, ToolsRoles.CreatePolicy.value)
          }
          onClick={() => {
            navigate(
              `/${pagesConfig.policies.link}/${pagesConfig.createPolicy.link}`
            );
          }}
          label="Create Policy"
          tooltipLabel={
            !isAbleToSeeUIElement(toolsRoles, ToolsRoles.CreatePolicy.value)
              ? Messages.Errors.Permission.Acting(ToolsRoles.CreatePolicy.name)
              : ''
          }
        />
      }
      secondaryButtons={
        <>
          <SharedIconButton
            variant="run"
            onClick={() => setOpenRunDialog(true)}
            disabled={
              selectedPolicies.length === 0 ||
              !canRun ||
              includesNewUserPolicies ||
              allRunnableAreRunning
            }
            tooltipLabel={getRunTooltip()}
          />
          <SharedIconButton
            variant="delete"
            onClick={() => {
              setOpenDeletePolicies(true);
            }}
            disabled={
              selectedPolicies.length === 0 ||
              !isAbleToSeeUIElement(toolsRoles, ToolsRoles.DeletePolicy.value)
            }
            tooltipLabel={
              !isAbleToSeeUIElement(toolsRoles, ToolsRoles.DeletePolicy.value)
                ? Messages.Errors.Permission.Acting(
                    ToolsRoles.DeletePolicy.name
                  )
                : 'Delete'
            }
          />
          <SharedIconButton
            variant="refresh"
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: ['Policies'] });
            }}
            tooltipLabel="Refresh"
          />
          <SharedGridStateIconButton
            gridStateKey="policiesGridStates"
            module={ModuleList.modules.automation.name}
            section={ModuleList.modules.automation.sections.policies}
          />
        </>
      }
    />
  );
}
